import React, { useEffect, useMemo, useRef } from 'react';
import { degToRad } from "three/src/math/MathUtils";
import { Box3, ClampToEdgeWrapping, DoubleSide, Float32BufferAttribute, Group, LinearFilter, LinearMipmapLinearFilter, Mesh, MeshBasicMaterial, Raycaster, TextureLoader, Vector3 } from "three";
import { DecalGeometry } from "three/examples/jsm/geometries/DecalGeometry";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { MODELS, getModelAvailableSides } from '../constants/models';
import { getDecalDimensions, getDecalOrientation, getSideDecalProjector, getSideSourceBounds, shouldSnapDecalToSurface } from '../constants/sideTextureMappings';

const NORMALIZED_MODEL_SIZE = 0.85;
const DECAL_DEPTH = 0.08;
const SIDE_DECAL_DEPTH = 0.18;
const DECAL_SURFACE_OFFSET = 0.002;
const DECAL_REBUILD_INTERVAL = 0.12;

const getObjectImageSource = (obj) =>
  obj?._originalElement?.currentSrc ||
  obj?._originalElement?.src ||
  obj?._element?.currentSrc ||
  obj?._element?.src ||
  obj?.src;

const keepOutwardFacingTriangles = (geometry, projector, acceptReversedNormals = false) => {
  const positionAttribute = geometry.getAttribute('position');
  const normalAttribute = geometry.getAttribute('normal');
  const uvAttribute = geometry.getAttribute('uv');
  const positions = [];
  const normals = [];
  const uvs = [];

  for (let index = 0; index < positionAttribute.count; index += 3) {
    const averageNormal = (
      normalAttribute.getComponent(index, projector.normalAxis === 'x' ? 0 : projector.normalAxis === 'y' ? 1 : 2) +
      normalAttribute.getComponent(index + 1, projector.normalAxis === 'x' ? 0 : projector.normalAxis === 'y' ? 1 : 2) +
      normalAttribute.getComponent(index + 2, projector.normalAxis === 'x' ? 0 : projector.normalAxis === 'y' ? 1 : 2)
    ) / 3;

    const outwardNormal = averageNormal * projector.normalSign;
    if (acceptReversedNormals ? Math.abs(outwardNormal) <= 0.1 : outwardNormal <= 0.1) {
      continue;
    }

    for (let vertexIndex = index; vertexIndex < index + 3; vertexIndex += 1) {
      positions.push(positionAttribute.getX(vertexIndex), positionAttribute.getY(vertexIndex), positionAttribute.getZ(vertexIndex));
      normals.push(normalAttribute.getX(vertexIndex), normalAttribute.getY(vertexIndex), normalAttribute.getZ(vertexIndex));
      uvs.push(uvAttribute.getX(vertexIndex), uvAttribute.getY(vertexIndex));
    }
  }

  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
};

function GridBox(props) {
  const selectedModel = props.model ?? MODELS[0];
  const availableSides = getModelAvailableSides(selectedModel);
  // CanvasTexture overlay state kept here for easy rollback:
  // const canvasRef = useRef(document.createElement("canvas"));
  // const textureRef = useRef(new CanvasTexture(canvasRef.current));
  const group = useRef();
  const decalGroup = useRef();
  const lastDecalSignature = useRef("");
  const pendingDecalSignature = useRef("");
  const wasEditingBrand = useRef(false);
  const decalRebuildElapsed = useRef(DECAL_REBUILD_INTERVAL);
  const textureCache = useRef(new Map());
  const textureLoader = useMemo(() => new TextureLoader(), []);
  const decalRaycaster = useMemo(() => new Raycaster(), []);
  // const context = useRef(canvasRef.current.getContext("2d"));
  const { scene } = useGLTF("/models/" + selectedModel.name + '/' + selectedModel.gltf);

  // textureRef.current.flipY = false;

  const { baseScene, rawSize, normalizedSize } = useMemo(() => {
    const createNormalizedScene = () => {
      const clonedScene = scene.clone(true);
      const wrapper = new Group();
      wrapper.add(clonedScene);

      clonedScene.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = true;
        }
      });

      const bounds = new Box3().setFromObject(wrapper);
      const center = bounds.getCenter(new Vector3());
      const size = bounds.getSize(new Vector3());
      const maxDimension = Math.max(size.x, size.y, size.z, 1e-6);
      const fitScale = NORMALIZED_MODEL_SIZE / maxDimension;

      clonedScene.position.sub(center);
      wrapper.scale.setScalar(fitScale);

      return wrapper;
    };

    const rawBounds = new Box3().setFromObject(scene);
    const nextRawSize = rawBounds.getSize(new Vector3());
    const maxDimension = Math.max(nextRawSize.x, nextRawSize.y, nextRawSize.z, 1e-6);
    const fitScale = NORMALIZED_MODEL_SIZE / maxDimension;
    const nextBaseScene = createNormalizedScene();
    // CanvasTexture overlay scene kept here for easy rollback:
    // const nextOverlayScene = createNormalizedScene();
    //
    // nextOverlayScene.traverse((node) => {
    //   if (!node.isMesh) {
    //     return;
    //   }
    //
    //   node.material = new MeshBasicMaterial({
    //     transparent: true,
    //     map: textureRef.current,
    //     side: DoubleSide,
    //     depthWrite: false,
    //     polygonOffset: true,
    //     polygonOffsetFactor: -1,
    //   });
    // });

    return {
      baseScene: nextBaseScene,
      rawSize: nextRawSize,
      normalizedSize: nextRawSize.clone().multiplyScalar(fitScale),
    };
  }, [scene, selectedModel.id]);

  const targetMeshesBySide = useMemo(() => {
    baseScene.updateMatrixWorld(true);

    const allMeshes = [];
    baseScene.traverse((node) => {
      if (node.isMesh) {
        allMeshes.push(node);
      }
    });

    const meshesBySide = availableSides.reduce((result, side) => {
      const projector = getSideDecalProjector(selectedModel, side);
      const sourceBounds = getSideSourceBounds(selectedModel, side);
      const nonExcludedMeshes = sourceBounds.excludedTargetMeshNamePrefixes
        ? allMeshes.filter((mesh) => !sourceBounds.excludedTargetMeshNamePrefixes.some(
            (prefix) => mesh.name.startsWith(prefix)
          ))
        : allMeshes;
      const namedTargetMeshes = sourceBounds.targetMeshNamePrefixes
        ? nonExcludedMeshes.filter((mesh) => sourceBounds.targetMeshNamePrefixes.some(
            (prefix) => mesh.name.startsWith(prefix)
          ))
        : [];
      const useAllNonExcludedMeshes = sourceBounds.excludedTargetMeshNamePrefixes?.length > 0;
      const sideMeshes = namedTargetMeshes.length > 0
        ? namedTargetMeshes
        : useAllNonExcludedMeshes
          ? nonExcludedMeshes
          : allMeshes.filter((mesh) => {
              const box = new Box3().setFromObject(mesh);
              const axisSize = normalizedSize[projector.normalAxis];
              return projector.normalSign > 0
                ? box.max[projector.normalAxis] >= axisSize / 2 - axisSize * 0.35
                : box.min[projector.normalAxis] <= -axisSize / 2 + axisSize * 0.35;
            });

      result[side] = sideMeshes.length > 0 ? sideMeshes : allMeshes;
      return result;
    }, {});

    meshesBySide.primarySurface = allMeshes.reduce((largestMesh, mesh) => {
      const size = new Box3().setFromObject(mesh).getSize(new Vector3());
      const volume = size.x * size.y * size.z;

      return !largestMesh || volume > largestMesh.volume
        ? { mesh, volume }
        : largestMesh;
    }, null)?.mesh;
    return meshesBySide;
  }, [availableSides, baseScene, normalizedSize, selectedModel]);

  useEffect(() => {
    lastDecalSignature.current = "";
    pendingDecalSignature.current = "";
    decalRebuildElapsed.current = DECAL_REBUILD_INTERVAL;
  }, [selectedModel.id]);

  useEffect(() => {
    props.onModelReady?.(selectedModel.id);
  }, [props.onModelReady, selectedModel.id]);

  useEffect(() => () => {
    textureCache.current.forEach((texture) => texture.dispose());
    textureCache.current.clear();
  }, []);

  useEffect(() => {
    props.onModelSizeChange?.({
      x: rawSize.x,
      y: rawSize.y,
      z: rawSize.z,
    });
  }, [props.onModelSizeChange, rawSize]);

  const getDecalTexture = (src) => {
    if (!src) {
      return null;
    }

    if (!textureCache.current.has(src)) {
      const texture = textureLoader.load(src);

      texture.generateMipmaps = true;
      texture.minFilter = LinearMipmapLinearFilter;
      texture.magFilter = LinearFilter;
      texture.anisotropy = 8;
      texture.wrapS = ClampToEdgeWrapping;
      texture.wrapT = ClampToEdgeWrapping;
      // The no-outline brown artwork contains transparent antialiased edge
      // pixels. Premultiplying before mipmap generation prevents their hidden
      // RGB values from filtering into a white fringe in the 3D decal while
      // preserving the intentionally opaque white border on black products.
      texture.premultiplyAlpha = true;
      texture.needsUpdate = true;

      textureCache.current.set(src, texture);
    }

    return textureCache.current.get(src);
  };

  const getDecalPlacement = (obj, side, sourceBounds) => {
    const projector = getSideDecalProjector(selectedModel, side);
    if (!projector) {
      return null;
    }

    const localX = obj.left - sourceBounds.left;
    const localY = obj.top - sourceBounds.top;
    const rawU = localX / sourceBounds.width;
    const u = projector.flipX ? 1 - rawU : rawU;
    const v = localY / sourceBounds.height;
    const position = new Vector3();
    const horizontalAxis = projector.horizontalAxis ?? (projector.normalAxis === "z" ? "x" : "z");
    const verticalAxis = projector.verticalAxis ?? "y";
    const verticalSign = projector.verticalSign ?? 1;
    const rawFaceWidth = normalizedSize[horizontalAxis];
    const rawFaceHeight = normalizedSize[verticalAxis];
    const surface = { ...getSideSourceBounds(selectedModel, side) };
    const surfaceWidth = rawFaceWidth * surface.widthScale;
    const surfaceHeight = rawFaceHeight * surface.heightScale;
    const surfaceOffsetX = rawFaceWidth * surface.offsetX;
    const surfaceOffsetY = rawFaceHeight * surface.offsetY;

    position[horizontalAxis] = surfaceOffsetX + (u - 0.5) * surfaceWidth;
    position[verticalAxis] = surfaceOffsetY + verticalSign * (0.5 - v) * surfaceHeight;
    position[projector.normalAxis] = projector.normalSign * (
      normalizedSize[projector.normalAxis] / 2 + DECAL_SURFACE_OFFSET
    );

    const { width: decalWidth, height: decalHeight } = getDecalDimensions({
      objectHeight: obj.height,
      objectScaleX: obj.scaleX,
      objectScaleY: obj.scaleY,
      objectWidth: obj.width,
      sourceBounds,
      surfaceHeight,
      surfaceWidth,
    });
    const angle = degToRad(obj.angle ?? 0);
    const orientation = getDecalOrientation(projector, angle);
    const snapHorizontalOnly =
      surface.horizontalOnlySnapAbove !== null &&
      v <= surface.horizontalOnlySnapAbove;

    if (snapHorizontalOnly) {
      position[verticalAxis] += verticalSign * rawFaceHeight * surface.upperSnapOffsetY;
    }

    return {
      position,
      orientation,
      snapHorizontalOnly,
      snapAcrossSideMeshes: surface.snapAcrossSideMeshes,
      projectAcrossSideMeshes: surface.projectAcrossSideMeshes,
      filterOutwardFacingTriangles: surface.filterOutwardFacingTriangles,
      acceptReversedOutwardNormals: surface.acceptReversedOutwardNormals,
      doubleSidedSurfaceRaycast: surface.doubleSidedSurfaceRaycast,
      size: new Vector3(
        Math.abs(decalWidth),
        Math.abs(decalHeight),
        surface.decalDepth ?? (projector.normalAxis === "x" ? SIDE_DECAL_DEPTH : DECAL_DEPTH)
      ),
    };
  };

  const snapPlacementToSurface = (placement, projector, targetMeshes) => {
    const outwardDirection = new Vector3();
    outwardDirection[projector.normalAxis] = projector.normalSign;
    const inwardDirection = outwardDirection.clone().negate();
    const primarySurface = targetMeshesBySide.primarySurface;
    const snapTargets = placement.snapAcrossSideMeshes
      ? targetMeshes
      : primarySurface
        ? [primarySurface]
        : [];
    const intersectSurface = (position) => {
      if (snapTargets.length === 0) {
        return undefined;
      }

      const originalMaterialSides = new Map();
      if (placement.doubleSidedSurfaceRaycast) {
        snapTargets.forEach((mesh) => {
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.filter(Boolean).forEach((material) => {
            if (!originalMaterialSides.has(material)) {
              originalMaterialSides.set(material, material.side);
            }
            material.side = DoubleSide;
          });
        });
      }

      decalRaycaster.set(position, inwardDirection);
      decalRaycaster.near = 0;
      decalRaycaster.far = Math.max(normalizedSize.x, normalizedSize.y, normalizedSize.z) * 2;
      try {
        return decalRaycaster.intersectObjects(snapTargets, false)[0];
      } finally {
        originalMaterialSides.forEach((side, material) => {
          material.side = side;
        });
      }
    };

    let intersection = intersectSurface(placement.position);

    if (!intersection) {
      // Rectangular 2D source bounds include pixels beyond rounded or tapered
      // corners. If an edge ray starts just outside the silhouette, find the
      // closest point that does intersect the leather body. Product-specific
      // tapered views can search horizontally so they retain their source
      // height; other placements keep the radial recovery calibrated for
      // rounded corners.
      const horizontalAxis = projector.horizontalAxis ?? (projector.normalAxis === "z" ? "x" : "z");
      const verticalAxis = projector.verticalAxis ?? "y";
      const outsidePosition = placement.position.clone();
      const insidePosition = placement.position.clone();
      insidePosition[horizontalAxis] = 0;
      if (!placement.snapHorizontalOnly) {
        insidePosition[verticalAxis] = 0;
      }
      let insideIntersection = intersectSurface(insidePosition);

      if (insideIntersection) {
        for (let iteration = 0; iteration < 8; iteration += 1) {
          const midpoint = outsidePosition.clone().lerp(insidePosition, 0.5);
          const midpointIntersection = intersectSurface(midpoint);

          if (midpointIntersection) {
            insidePosition.copy(midpoint);
            insideIntersection = midpointIntersection;
          } else {
            outsidePosition.copy(midpoint);
          }
        }

        const inwardNudge = insidePosition
          .clone()
          .multiplyScalar(-1);
        inwardNudge[projector.normalAxis] = 0;
        if (placement.snapHorizontalOnly) {
          inwardNudge[verticalAxis] = 0;
        }
        if (inwardNudge.lengthSq() > 0) {
          inwardNudge
            .normalize()
            .multiplyScalar(Math.min(placement.size.x, placement.size.y) * 0.85);
          const nudgedPosition = insidePosition.clone().add(inwardNudge);
          const nudgedIntersection = intersectSurface(nudgedPosition);
          if (nudgedIntersection) {
            insideIntersection = nudgedIntersection;
          }
        }
        intersection = insideIntersection;
      }
    }

    if (intersection) {
      placement.position
        .copy(intersection.point)
        .addScaledVector(outwardDirection, DECAL_SURFACE_OFFSET);
    }

    return intersection?.object;
  };

  const getDecalSignature = () =>
    availableSides.map((side) => {
      const canvas = props.canvases?.[side];
      if (!canvas) {
        return `${side}=`;
      }

      return `${side}=${canvas.getObjects().map((obj) => [
        getObjectImageSource(obj),
        obj.left,
        obj.top,
        obj.width,
        obj.height,
        obj.scaleX,
        obj.scaleY,
        obj.angle,
      ].join("|")).join("~")}`;
    }).join("&");

  const clearDecals = () => {
    if (!decalGroup.current) {
      return;
    }

    const disposedMaterials = new Set();
    decalGroup.current.children.forEach((child) => {
      child.geometry.dispose();
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        if (!disposedMaterials.has(material)) {
          material.dispose();
          disposedMaterials.add(material);
        }
      });
    });
    decalGroup.current.clear();
  };

  const rebuildDecals = () => {
    clearDecals();
    baseScene.updateMatrixWorld(true);

    availableSides.forEach((side) => {
      const canvas = props.canvases?.[side];
      if (!canvas) {
        return;
      }

      const sourceBounds = getSideSourceBounds(selectedModel, side);
      const projector = getSideDecalProjector(selectedModel, side);
      const targetMeshes = targetMeshesBySide[side] ?? [];

      canvas.getObjects().forEach((obj) => {
        const texture = getDecalTexture(getObjectImageSource(obj));
        const placement = getDecalPlacement(obj, side, sourceBounds);
        if (!texture || !placement) {
          return;
        }

        const snappedTarget = shouldSnapDecalToSurface(selectedModel, side)
          ? snapPlacementToSurface(placement, projector, targetMeshes)
          : undefined;
        // The tote Top is assembled from several adjacent meshes. Snapping to
        // the nearest one establishes depth, but the decal projector must run
        // against every eligible component so a brand cannot disappear at a
        // zipper or leather-panel boundary.
        const decalTargetMeshes = placement.projectAcrossSideMeshes
          ? targetMeshes
          : snappedTarget
            ? [snappedTarget]
            : targetMeshes;

        const material = new MeshBasicMaterial({
          map: texture,
          transparent: true,
          premultipliedAlpha: true,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -4,
          side: DoubleSide,
          alphaTest: 0.15
        });

        let materialUsed = false;
        decalTargetMeshes.forEach((targetMesh) => {
          const geometry = new DecalGeometry(
            targetMesh,
            placement.position,
            placement.orientation,
            placement.size
          );

          if (projector.normalAxis === 'y' || placement.filterOutwardFacingTriangles) {
            keepOutwardFacingTriangles(
              geometry,
              projector,
              placement.acceptReversedOutwardNormals
            );
          }

          if (geometry.attributes.position.count === 0) {
            geometry.dispose();
            return;
          }

          decalGroup.current.add(new Mesh(geometry, material));
          materialUsed = true;
        });

        if (!materialUsed) {
          material.dispose();
        }
      });
    });
  };

  // CanvasTexture drawing helpers kept here for easy rollback:
  //
  // const drawMappedBrand = (ctx, obj, sourceBounds, mapping, localCenterX) => {
  //   const width = obj.width;
  //   const height = obj.height;
  //   const scaleX = obj.scaleX;
  //   const scaleY = obj.scaleY;
  //   const src = obj._originalElement.src;
  //   const centerX = localCenterX;
  //   const centerY = obj.top - sourceBounds.top;
  //   const mappedCenterX = mapping.flipX ? sourceBounds.width - centerX : centerX;
  //   const mappedCenterY = mapping.flipY ? sourceBounds.height - centerY : centerY;
  //
  //   const img = new Image();
  //   img.src = src;
  //
  //   const u = mappedCenterX / sourceBounds.width;
  //   const v = mappedCenterY / sourceBounds.height;
  //
  //   ctx.save();
  //   if (mapping.swapAxes) {
  //     ctx.translate(
  //       mapping.originY + mapping.height * v,
  //       mapping.originX + mapping.width * u
  //     );
  //   } else {
  //     ctx.translate(
  //       mapping.originX + mapping.width * u,
  //       mapping.originY + mapping.height * v
  //     );
  //   }
  //
  //   ctx.rotate(degToRad(mapping.rotation + obj.angle));
  //
  //   const drawWidth = Math.abs(mapping.width) * (width * scaleX / sourceBounds.width);
  //   const drawHeight = Math.abs(mapping.height) * (height * scaleY / sourceBounds.height);
  //   ctx.drawImage(
  //     img,
  //     -drawWidth / 2,
  //     -drawHeight / 2,
  //     drawWidth,
  //     drawHeight
  //   );
  //   ctx.restore();
  // };
  //
  // const drawSideBrands = (ctx, fabricCanvas, side) => {
  //   if (!fabricCanvas) return;
  //
  //   const sourceBounds = getSideSourceBounds(side);
  //   const objects = fabricCanvas.getObjects();
  //   const mappings = getSideTextureMappings(selectedModel.name, side);
  //
  //   for (let i = 0; i < objects.length; i++) {
  //     const obj = objects[i];
  //     const localCenterX = obj.left - sourceBounds.left;
  //     const defaultMapping = mappings.find((candidate) =>
  //       candidate.condition ? candidate.condition(localCenterX, sourceBounds.width) : true
  //     );
  //     const mapping = { ...defaultMapping };
  //     if (!mapping) continue;
  //     const mappedCenterX = mapping.transformLeft ? mapping.transformLeft(localCenterX, sourceBounds.width) : localCenterX;
  //     drawMappedBrand(ctx, obj, sourceBounds, mapping, mappedCenterX);
  //   }
  // };

  useFrame((state, delta) => {
    const nextDecalSignature = getDecalSignature();
    if (nextDecalSignature !== lastDecalSignature.current) {
      pendingDecalSignature.current = nextDecalSignature;
    }

    const finishedEditingBrand = wasEditingBrand.current && !props.isEditingBrand;
    wasEditingBrand.current = props.isEditingBrand;
    decalRebuildElapsed.current += delta;
    if (
      pendingDecalSignature.current &&
      !props.isEditingBrand &&
      (
        finishedEditingBrand ||
        lastDecalSignature.current === "" ||
        decalRebuildElapsed.current >= DECAL_REBUILD_INTERVAL
      )
    ) {
      lastDecalSignature.current = pendingDecalSignature.current;
      pendingDecalSignature.current = "";
      decalRebuildElapsed.current = 0;
      rebuildDecals();
    }

    // CanvasTexture frame update kept here for easy rollback:
    // canvasRef.current.width = canvasSize;
    // canvasRef.current.height = canvasSize;
    // const ctx = context.current;
    // ctx.clearRect(0, 0, canvasSize, canvasSize);
    //
    // availableSides.forEach((side) => {
    //   drawSideBrands(ctx, props.canvases?.[side], side);
    // });

    // textureRef.current.needsUpdate = true;
  });
  
  return (
    <group ref={group} {...props}>
      <primitive object={baseScene} />
      {/* CanvasTexture overlay kept here for easy rollback: */}
      {/* <primitive object={overlayScene} /> */}
      <group ref={decalGroup} />
    </group>
  );
}

export default GridBox;
